import {Color} from '@benchling/nucleus-ui';
import {Button as MantineButton} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';

import * as AuditActions from 'audit/AuditActions';
import {
  AUDIT_LOG_EMPTY_CELL_VALUE,
  AUDIT_LOG_NEW_VALUE_COLUMN,
  AUDIT_LOG_OLD_VALUE_COLUMN,
} from 'audit/AuditConstants';
import {ActionFilterCriteria, AuditLogFormat} from 'audit/AuditDAO';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import Button from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import ResultsPager from 'ui-library/ResultsPager';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import SortableHeaderCell, {
  SORT_ICON_DOWN,
  SORT_ICON_UP,
  SortIcon,
} from 'ui-library/simple-table/SortableHeaderCell';

import AuditLogColumnVisibilityDropdown from './AuditLogColumnVisibilityDropdown';
import AuditLogActionFilterDropdown from './filters/AuditLogActionFilterDropdown';
import AuditLogDateRangeFilterDropdown from './filters/AuditLogDateRangeFilterDropdown';
import AuditLogReasonCodeFilterDropdown from './filters/AuditLogReasonCodeFilterDropdown';
import AuditLogUserFilterDropdown from './filters/AuditLogUserFilterDropdown';
import JsonFormattedCellValue from './JsonFormattedCellValue';
import useAuditLogData from './useAuditLogData';
import useAuditLogFilters from './useAuditLogFilters';
import useColumnVisibility from './useColumnVisibility';

/** Options passed to the audit log modal and export flow; `modalTitleOverride` is UI-only and stripped before API calls. */
export interface AuditLogViewModalAdditionalOptions {
  resultsWarehouseIds?: Array<string>;
  relatedResultsModelId?: string;
  /** Override modal title when metadata would show a less accurate label. */
  modalTitleOverride?: string;
}

export interface AuditLogViewModalProps {
  onRequestClose: () => void;
  ids: Array<string>;
  additionalOptions?: AuditLogViewModalAdditionalOptions;
}

const PAGE_SIZE = 50;
const EMPTY_ACTION_CRITERIA: Array<ActionFilterCriteria> = [];
const TIMESTAMP_COLUMN_PREFIX = 'Timestamp';

export default function AuditLogViewModal(props: AuditLogViewModalProps): JSX.Element {
  const [timestampSortIcon, setTimestampSortIcon] = useState<SortIcon>(SORT_ICON_UP);
  const sortAscending = timestampSortIcon === SORT_ICON_UP;

  const handleToggleTimestampSort = useCallback(() => {
    setTimestampSortIcon((prev) => (prev === SORT_ICON_UP ? SORT_ICON_DOWN : SORT_ICON_UP));
  }, []);

  const {
    dateRange,
    setDateRange,
    dateMode,
    setDateMode,
    committedDateFilter,
    selectedUserIds,
    setSelectedUserIds,
    selectedActionValues,
    setSelectedActionValues,
    selectedReasonCodes,
    setSelectedReasonCodes,
    filterUsers,
    filterActions,
    filterReasonCodes,
    filterOptionsError,
    hasActiveFilters,
    clearAllFilters,
    displayDateFormat,
  } = useAuditLogFilters({
    ids: props.ids,
    resultsWarehouseIds: props.additionalOptions?.resultsWarehouseIds,
    relatedResultsModelId: props.additionalOptions?.relatedResultsModelId,
  });

  const selectedActionCriteria: Array<ActionFilterCriteria> = useMemo(() => {
    if (selectedActionValues.length === 0) {
      return EMPTY_ACTION_CRITERIA;
    }
    const selectedSet = new Set(selectedActionValues);
    return filterActions
      .filter((action) => selectedSet.has(action.displayName))
      .flatMap((action) => action.filters);
  }, [selectedActionValues, filterActions]);

  const {state, allColumns, loadAuditData} = useAuditLogData({
    ids: props.ids,
    pageSize: PAGE_SIZE,
    resultsWarehouseIds: props.additionalOptions?.resultsWarehouseIds,
    relatedResultsModelId: props.additionalOptions?.relatedResultsModelId,
    userIds: selectedUserIds,
    actions: selectedActionCriteria,
    reasonCodes: selectedReasonCodes,
    startDate: committedDateFilter.startDateISO,
    endDate: committedDateFilter.endDateISO,
    sortAscending,
  });
  const {isLoading, auditData, currentPage, error} = state;

  const {hiddenColumns, visibleColumns, handleColumnToggle, handleHideAll, handleShowAll} =
    useColumnVisibility(allColumns);

  const filtersDisabled =
    isLoading || filterOptionsError || (!hasActiveFilters && (!auditData || auditData.data.length === 0));

  const renderMainContent = (): JSX.Element => {
    if (isLoading) {
      return (
        <div className={css(styles.loadingContainer)}>
          <HorizontalThrobber />
        </div>
      );
    }

    if (error) {
      return (
        <div className={css(styles.errorContainer)}>
          <div className={css(styles.errorText)}>{error}</div>
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            onClick={() => {
              loadAuditData(currentPage);
            }}
            size={ButtonSize.MEDIUM}
            text="Retry"
          />
        </div>
      );
    }

    if (!auditData || auditData.data.length === 0) {
      return (
        <div className={css(styles.emptyContainer)}>
          <BenchlingIcon icon="audit-trail" className={css(styles.emptyIcon)} />
          <div className={css(styles.emptyText)}>No audit log entries found.</div>
        </div>
      );
    }

    const {data} = auditData;

    return (
      <Table
        numRows={visibleColumns.length ? data.length : 0}
        emptyTableText="All columns hidden. Select columns to display."
        getRowKey={(rowIndex) => rowIndex.toString()}
        isCompact={true}
        showBorder={false}
        overflowVisible={true}
        frozenHeader={true}
      >
        <Column
          key="row-number"
          header=""
          renderCell={(rowIndex: number) => (
            <Cell>
              <div className={css(styles.cellContent, styles.rowNumberCell)}>
                {currentPage * PAGE_SIZE + rowIndex + 1}
              </div>
            </Cell>
          )}
        />
        {visibleColumns.map((column: string) => (
          <Column
            key={column}
            header={
              column.startsWith(TIMESTAMP_COLUMN_PREFIX) ? (
                <SortableHeaderCell
                  sortIcon={timestampSortIcon}
                  onToggleSort={handleToggleTimestampSort}
                  ariaSort={sortAscending ? 'ascending' : 'descending'}
                >
                  {column}
                </SortableHeaderCell>
              ) : (
                column
              )
            }
            renderCell={(rowIndex: number) => (
              <Cell>
                <div className={css(styles.cellContent)}>
                  {column === AUDIT_LOG_OLD_VALUE_COLUMN || column === AUDIT_LOG_NEW_VALUE_COLUMN ? (
                    <JsonFormattedCellValue value={data[rowIndex]?.[column]} />
                  ) : (
                    data[rowIndex]?.[column] || AUDIT_LOG_EMPTY_CELL_VALUE
                  )}
                </div>
              </Cell>
            )}
          />
        ))}
      </Table>
    );
  };

  const renderStickyHeader = (): JSX.Element => (
    <div className={css(styles.toolbarContainer)}>
      <div className={css(styles.rowGroup)}>
        <AuditLogDateRangeFilterDropdown
          value={dateRange}
          onChange={setDateRange}
          mode={dateMode}
          onModeChange={setDateMode}
          disabled={filtersDisabled}
          displayDateFormat={displayDateFormat}
        />
        <AuditLogUserFilterDropdown
          users={filterUsers}
          selectedUserIds={selectedUserIds}
          onChange={setSelectedUserIds}
          disabled={filtersDisabled}
        />
        <AuditLogActionFilterDropdown
          actions={filterActions}
          selectedActionValues={selectedActionValues}
          onChange={setSelectedActionValues}
          disabled={filtersDisabled}
        />
        {filterReasonCodes != null && (
          <AuditLogReasonCodeFilterDropdown
            reasonCodes={filterReasonCodes}
            selectedReasonCodes={selectedReasonCodes}
            onChange={setSelectedReasonCodes}
            disabled={filtersDisabled}
          />
        )}
        {hasActiveFilters && (
          <MantineButton
            variant="ghost"
            size="sm"
            leftIcon={<BenchlingIcon icon="xmark-large" />}
            onClick={clearAllFilters}
          >
            Clear all
          </MantineButton>
        )}
      </div>
      <div className={css(styles.rowGroup)}>
        <AuditLogColumnVisibilityDropdown
          allColumns={allColumns}
          hiddenColumns={hiddenColumns}
          onColumnToggle={handleColumnToggle}
          onHideAll={handleHideAll}
          onShowAll={handleShowAll}
          disabled={filtersDisabled}
        />
        <Dropdown
          menu={
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM} dropLeft={true}>
              <Option
                dataTestAttrs={{component: 'AuditLogViewModal', element: 'exportCsvOption'}}
                onSelect={() => {
                  AuditActions.initiateAuditLogExport(
                    props.ids,
                    AuditLogFormat.CSV,
                    props.additionalOptions || {}
                  );
                }}
              >
                Export CSV
              </Option>
              <Option
                dataTestAttrs={{component: 'AuditLogViewModal', element: 'exportPdfOption'}}
                onSelect={() => {
                  AuditActions.initiateAuditLogExport(
                    props.ids,
                    AuditLogFormat.PDF,
                    props.additionalOptions || {}
                  );
                }}
              >
                Export PDF
              </Option>
            </Menu>
          }
          closeOnInsideClick={true}
        >
          <Button
            dataTestAttrs={{component: 'AuditLogViewModal', element: 'exportButton'}}
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            icon="download"
            size={ButtonSize.SMALL}
            text="Export"
            isDropdown={true}
          />
        </Dropdown>
      </div>
    </div>
  );

  return (
    <Modal
      enableRedesign={true}
      title={props.additionalOptions?.modalTitleOverride || auditData?.objectMetadata?.title || 'Audit Log'}
      onRequestClose={props.onRequestClose}
      extraLarge={true}
      scrollable={false}
      expandHeight={false}
      dataTestAttrs={{component: 'AuditLogViewModal', element: 'root'}}
    >
      <div className={css(styles.modalContainer)}>
        {renderStickyHeader()}
        <div className={css(styles.scrollableContainer)}>{renderMainContent()}</div>
        {auditData?.pagination && (
          <ResultsPager
            allowFastForwardAndBackward={true}
            isLoading={isLoading}
            pagination={{
              offset: currentPage * PAGE_SIZE,
              limit: PAGE_SIZE,
              total: auditData.pagination.total,
            }}
            setOffset={(newOffset: number) => {
              const newPage = Math.floor(newOffset / PAGE_SIZE);
              loadAuditData(newPage);
            }}
          />
        )}
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 220px)',
  },
  toolbarContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  rowGroup: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    alignItems: 'center',
  },
  scrollableContainer: {
    flex: 1,
    overflow: 'auto',
    marginBottom: '8px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
    textAlign: 'center',
  },
  errorText: {
    marginBottom: '16px',
    color: Color.GRAY_400,
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    color: Color.GRAY_50,
    marginBottom: '16px',
  },
  emptyText: {
    color: Color.GRAY_400,
  },
  cellContent: {
    paddingTop: '10px',
    paddingBottom: '10px',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    lineHeight: '1.5',
    fontSize: '14px',
    minHeight: '20px',
    maxWidth: '400px',
    width: 'max-content',
  },
  rowNumberCell: {
    textAlign: 'center',
    color: Color.GRAY_400,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    width: '30px',
  },
});
